/**
 * L1J Web Client - CharacterStatsPanel Component (Lineage Classic Style)
 *
 * 좌하단에 표시되는 캐릭터 정보 패널:
 *   - 현재 위치 / 맵 이름
 *   - 레벨
 *   - EXP %
 *   - 카르마 (Chaotic / Neutral / Knight / Hero)
 * 카메라 스크롤과 무관하게 화면에 고정.
 *
 * **이 패널은 자기 값을 스스로 구독한다.** 예전에는 생성자에서 `Lv. 1` / `0%` /
 * `Neutral 0`을 하드코딩해 두고 GameScene이 갱신해 주기를 기다렸는데, GameScene이
 * 넘겨 주는 `player:stats`에는 exp와 lawful이 실려 있지 않다(GameState가 HP/MP/레벨만
 * 담아 발행한다). 그래서 월드에 들어와도 Lv.60 캐릭터가 계속 `Lv. 1 / 0% / Neutral 0`
 * 으로 남아 있었고, HP가 변할 때마다 오히려 0으로 덮어써지기까지 했다.
 *
 * 이제 스폰 시점의 확정값을 다음 경로로 직접 받는다:
 *   - `gameEvents('player:detail')` — S_OwnCharStatus(own_char_status)의 전체 스탯.
 *     level / exp / lawful이 모두 들어 있고, 월드 진입 직후 반드시 한 번 온다.
 *   - `networkEvents('exp_update')` — S_Exp. 경험치를 얻을 때마다 { level, exp }.
 *   - `gameEvents('entity:lawful')` — S_Lawful. 자기 캐릭터(isPlayer)일 때만 반영.
 *
 * 서버 값이 한 번이라도 도착한 뒤에는 외부 `setStats()`의 level/exp/lawful을 무시한다
 * (GameScene이 `player:stats`마다 `lawful: 0`, `exp: 0`을 채워 넘기므로 그대로 받으면
 * 방금 받은 진짜 값이 지워진다). 위치(location)만은 외부에서 계속 설정할 수 있다.
 */

import Phaser from 'phaser';
import { gameEvents, networkEvents } from '../../core/EventEmitter';
import { gameState } from '../../core/GameState';
import type { PlayerDetailStats } from '../../core/types';
import { getLevelProgress } from '../../data/expTable';

export interface CharacterStatsData {
  location: string;
  level: number;
  exp: number;
  /**
   * @deprecated 서버는 다음 레벨까지 필요한 경험치를 보내지 않는다.
   * 진행률은 `data/expTable.ts`의 누적 경험치 표로 계산하므로 이 값은 무시된다.
   * (기존 호출부와의 호환을 위해 필드만 남겨 둔다.)
   */
  maxExp?: number;
  lawful: number;
}

export class CharacterStatsPanel extends Phaser.GameObjects.Container {
  private panelWidth: number = 200;
  private panelHeight: number = 86;

  private locationText!: Phaser.GameObjects.Text;
  private levelText!: Phaser.GameObjects.Text;
  private expText!: Phaser.GameObjects.Text;
  private lawfulText!: Phaser.GameObjects.Text;
  private expBar!: Phaser.GameObjects.Graphics;

  /** 서버에서 받은 현재 값. 아직 못 받았으면 null (화면에는 `-`로 둔다). */
  private level: number | null = null;
  private exp: number | null = null;
  private lawful: number | null = null;

  /**
   * 서버 값을 한 번이라도 받았는지. true가 되면 외부 `setStats()`의
   * level/exp/lawful은 무시한다 (위 클래스 주석 참조).
   */
  private hasServerStats: boolean = false;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this as unknown as Phaser.GameObjects.Container);

    this.setScrollFactor(0);
    this.setDepth(100);

    this.createUI();
    this.subscribe();
  }

  private createUI(): void {
    const w = this.panelWidth;
    const h = this.panelHeight;

    // 배경
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x1a1a2e, 0.85);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 6);
    bg.lineStyle(2, 0x4a3a1a, 1);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 6);
    this.add(bg);

    // 위치명 (제목)
    // ⚠ 아직 하드코딩이다. 서버는 map_id(숫자)만 보내고 이름은 보내지 않는데,
    //    맵 이름의 근거가 될 데이터(DB `map_ids.name`)를 클라이언트에 아직 뽑아 두지
    //    않았다. 지어내지 않기 위해 기본 문구를 그대로 둔다.
    this.locationText = this.scene.add.text(-w / 2 + 10, -h / 2 + 12, '말하는 섬 마을', {
      fontSize: '13px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      fontStyle: 'bold'
    });
    this.locationText.setOrigin(0, 0.5);
    this.add(this.locationText);

    // 레벨 — 서버 값이 오기 전까지는 `-`. 예전처럼 `Lv. 1`을 보여 주면
    // 실제 레벨과 다른 값이 한동안 사실인 것처럼 남는다.
    this.levelText = this.scene.add.text(-w / 2 + 10, -h / 2 + 32, 'Lv. -', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff'
    });
    this.levelText.setOrigin(0, 0.5);
    this.add(this.levelText);

    // EXP 바
    const expBarW = w - 90;
    const expBarX = -w / 2 + 10;
    const expBarY = -h / 2 + 50;

    const expBg = this.scene.add.graphics();
    expBg.fillStyle(0x333300, 1);
    expBg.fillRoundedRect(expBarX, expBarY - 3, expBarW, 6, 2);
    this.add(expBg);

    this.expBar = this.scene.add.graphics();
    this.add(this.expBar);

    // EXP 텍스트
    this.expText = this.scene.add.text(w / 2 - 10, expBarY, '-', {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700'
    });
    this.expText.setOrigin(1, 0.5);
    this.add(this.expText);

    // 카르마
    this.lawfulText = this.scene.add.text(-w / 2 + 10, -h / 2 + 70, '-', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#aaaaaa'
    });
    this.lawfulText.setOrigin(0, 0.5);
    this.add(this.lawfulText);
  }

  // ==================== 서버 값 구독 ====================

  /** S_OwnCharStatus 전체 스탯 (월드 진입 직후 한 번은 반드시 온다). */
  private readonly onPlayerDetail = (stats: PlayerDetailStats): void => {
    if (!stats) return;
    this.applyServerStats(stats.level, stats.exp, stats.lawful);
  };

  /** S_Exp — 경험치를 얻을 때마다. lawful은 실려 오지 않는다. */
  private readonly onExpUpdate = (data: { level: number; exp: number }): void => {
    if (typeof data?.level !== 'number') return;
    this.applyServerStats(data.level, data.exp, undefined);
  };

  /**
   * S_Lawful — 성향치 변경. 자기 캐릭터만 반영한다.
   *
   * `player:detail`로도 성향 변화가 흘러오지만, 그 경로는 S_OwnCharStatus를 이미
   * 받아 둔 뒤에만 동작한다(GameState.setEntityLawful). 순서가 뒤집혀도 놓치지
   * 않도록 이쪽도 함께 듣는다.
   */
  private readonly onEntityLawful = (data: { lawful: number; isPlayer: boolean }): void => {
    if (!data?.isPlayer) return;
    this.applyServerStats(undefined, undefined, data.lawful);
  };

  private subscribe(): void {
    gameEvents.on('player:detail', this.onPlayerDetail);
    networkEvents.on('exp_update', this.onExpUpdate);
    gameEvents.on('entity:lawful', this.onEntityLawful);

    // 패널이 만들어지기 전에 이미 S_OwnCharStatus가 도착했을 수 있다
    // (씬 생성 순서에 따라 갈린다). 그 경우 이벤트는 다시 오지 않으므로
    // 보관된 스냅샷으로 초기화한다.
    const snapshot = gameState.getDetailStats();
    if (snapshot) this.onPlayerDetail(snapshot);
  }

  private unsubscribe(): void {
    gameEvents.off('player:detail', this.onPlayerDetail);
    networkEvents.off('exp_update', this.onExpUpdate);
    gameEvents.off('entity:lawful', this.onEntityLawful);
  }

  /**
   * 서버에서 온 값을 반영한다. undefined인 항목은 기존 값을 유지한다.
   */
  private applyServerStats(
    level: number | undefined,
    exp: number | undefined,
    lawful: number | undefined
  ): void {
    let changed = false;

    if (typeof level === 'number' && level !== this.level) {
      this.level = level;
      changed = true;
    }
    if (typeof exp === 'number' && exp !== this.exp) {
      this.exp = exp;
      changed = true;
    }
    if (typeof lawful === 'number' && lawful !== this.lawful) {
      this.lawful = lawful;
      changed = true;
    }

    // 값이 그대로여도 "서버 값을 받았다"는 사실은 기록해야 한다 —
    // 이후 GameScene이 넘기는 0들을 막는 근거가 이 플래그다.
    this.hasServerStats = true;

    if (changed) this.render();
  }

  // ==================== 외부 갱신 ====================

  /**
   * 외부(GameScene)에서 값을 밀어 넣는다.
   *
   * level/exp/lawful은 **서버 값을 한 번이라도 받은 뒤에는 무시한다.** 호출부가
   * 모르는 값을 0으로 채워 넘기기 때문이다(`player:stats`에는 exp/lawful이 없다).
   * location은 구독 경로가 없으므로 언제나 반영한다.
   */
  public setStats(data: Partial<CharacterStatsData>): void {
    if (data.location !== undefined) {
      this.locationText.setText(data.location);
    }

    if (this.hasServerStats) return;

    let changed = false;
    if (data.level !== undefined && data.level !== this.level) {
      this.level = data.level;
      changed = true;
    }
    if (data.exp !== undefined && data.exp !== this.exp) {
      this.exp = data.exp;
      changed = true;
    }
    if (data.lawful !== undefined && data.lawful !== this.lawful) {
      this.lawful = data.lawful;
      changed = true;
    }

    if (changed) this.render();
  }

  // ==================== 렌더링 ====================

  private render(): void {
    this.levelText.setText(this.level === null ? 'Lv. -' : `Lv. ${this.level}`);
    this.updateExp();
    this.updateLawful();
  }

  /**
   * 경험치 진행률.
   *
   * 서버는 누적 경험치만 보내고 다음 레벨까지 필요한 양은 보내지 않으므로,
   * 자동 생성된 누적 경험치 표(`data/expTable.ts`)로 직접 계산한다.
   */
  private updateExp(): void {
    const w = this.panelWidth;
    const h = this.panelHeight;
    const expBarW = w - 90;
    const expBarX = -w / 2 + 10;
    const expBarY = -h / 2 + 50;

    this.expBar.clear();

    if (this.level === null || this.exp === null) {
      this.expText.setText('-');
      return;
    }

    const { ratio } = getLevelProgress(this.level, this.exp);

    if (ratio > 0) {
      // 갓 레벨업한 직후처럼 비율이 아주 작아도 막대가 보이게 최소 1px는 그린다.
      const fillWidth = Math.max(1, expBarW * ratio);
      this.expBar.fillStyle(0xffd700, 1);
      this.expBar.fillRoundedRect(expBarX, expBarY - 3, fillWidth, 6, 2);
    }

    // 라니지의 경험치 표시는 소수점 둘째 자리까지다. 한 레벨의 폭이 수천만이라
    // 정수 %로 자르면 방금 레벨업한 구간이 통째로 `0%`가 되어 버린다.
    this.expText.setText(ratio >= 1 ? '100%' : `${(ratio * 100).toFixed(2)}%`);
  }

  private updateLawful(): void {
    if (this.lawful === null) {
      this.lawfulText.setText('-');
      this.lawfulText.setStyle({ color: '#aaaaaa' });
      return;
    }

    const lawful = this.lawful;
    let text: string;
    let color: string;

    if (lawful >= 750) {
      text = `Hero ${lawful}`;
      color = '#ffd700';
    } else if (lawful >= 500) {
      text = `Knight ${lawful}`;
      color = '#44ff44';
    } else if (lawful >= 0) {
      text = `Neutral ${lawful}`;
      color = '#aaaaaa';
    } else {
      text = `Chaotic ${lawful}`;
      color = '#ff4444';
    }

    this.lawfulText.setText(text);
    this.lawfulText.setStyle({ color });
  }

  // ==================== 정리 ====================

  /**
   * 전역 이벤트 버스를 구독하므로 파괴 시 반드시 해제한다.
   * (씬을 다시 시작할 때 죽은 Text 객체를 만지는 리스너가 남으면 안 된다.)
   */
  public destroy(fromScene?: boolean): void {
    this.unsubscribe();
    super.destroy(fromScene);
  }
}
